const JAPANESE_PREFECTURES = [
  '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県', '茨城県',
  '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県', '新潟県', '富山県',
  '石川県', '福井県', '山梨県', '長野県', '岐阜県', '静岡県', '愛知県', '三重県',
  '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県', '鳥取県', '島根県',
  '岡山県', '広島県', '山口県', '徳島県', '香川県', '愛媛県', '高知県', '福岡県',
  '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県'
];

const KANJI_DIGITS: Record<string, number> = {
  '〇': 0,
  '一': 1,
  '二': 2,
  '三': 3,
  '四': 4,
  '五': 5,
  '六': 6,
  '七': 7,
  '八': 8,
  '九': 9
};

export interface AddressCoordinates {
  lat: number;
  lng: number;
}

export function legacyJapaneseAddressCoordinates(address: unknown, geolocation: unknown): AddressCoordinates | null {
  const addressText = String(address || '').trim();
  if (!addressText.includes(',') || (!addressText.includes('日本') && !addressText.includes('〒'))) {
    return null;
  }

  try {
    const location = typeof geolocation === 'string' ? JSON.parse(geolocation) : geolocation as any;
    const rawLat = location?.lat;
    const rawLng = location?.lng ?? location?.lon;
    if (rawLat == null || rawLng == null || String(rawLat).trim() === '' || String(rawLng).trim() === '') {
      return null;
    }
    const lat = Number(rawLat);
    const lng = Number(rawLng);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  } catch {
    return null;
  }
}

function value(address: Record<string, unknown>, key: string): string | undefined {
  const raw = address[key];
  if (raw == null || String(raw).trim() === '') {
    return undefined;
  }
  return String(raw).trim();
}

function prefectureFromIsoCode(address: Record<string, unknown>): string | undefined {
  const isoCode = value(address, 'ISO3166-2-lvl4') || value(address, 'ISO3166-2-lvl3');
  const match = isoCode?.match(/^JP-(\d{2})$/i);
  if (!match) {
    return undefined;
  }

  return JAPANESE_PREFECTURES[Number(match[1]) - 1];
}

function kanjiNumberToArabic(input: string): string {
  if (!input || !/^[〇一二三四五六七八九十]+$/.test(input)) {
    return input;
  }

  if (!input.includes('十')) {
    return input.split('').map(character => KANJI_DIGITS[character]).join('');
  }

  const [tensText, onesText] = input.split('十');
  const tens = tensText ? KANJI_DIGITS[tensText] : 1;
  const ones = onesText ? KANJI_DIGITS[onesText] : 0;
  if (tens == null || ones == null) {
    return input;
  }
  return String((tens * 10) + ones);
}

function normalizeJapaneseAddressPart(input: string): string {
  return input
    .replace(/[０-９]/g, character => String(character.charCodeAt(0) - 0xFEE0))
    .replace(/([〇一二三四五六七八九十]+)丁目/g, (_, numberText: string) => `${kanjiNumberToArabic(numberText)}丁目`);
}

function normalizeJapaneseNumericPart(input: string): string {
  return normalizeJapaneseAddressPart(input).replace(/[\-−ー―‐]/g, '－');
}

function normalizePostcode(input: string): string {
  return input
    .replace(/[０-９]/g, character => String(character.charCodeAt(0) - 0xFEE0))
    .replace(/[－−ー―‐]/g, '-');
}

function pickLocality(address: Record<string, unknown>): string | undefined {
  const candidates = [
    value(address, 'neighbourhood'),
    value(address, 'quarter'),
    value(address, 'suburb'),
    value(address, 'residential'),
    value(address, 'road')
  ].filter((part): part is string => !!part);

  const chome = candidates.find(part => part.includes('丁目'));
  if (chome) {
    const road = value(address, 'road');
    return road && /^([〇一二三四五六七八九十０-９0-9]+丁目)$/.test(chome)
      ? `${road}${chome}`
      : chome;
  }

  return candidates[0];
}

/** Format structured Nominatim data using Japanese postal-address conventions. */
export function formatJapaneseAddress(address: Record<string, unknown> | null | undefined): string | null {
  if (!address || typeof address !== 'object') {
    return null;
  }

  const countryCode = value(address, 'country_code')?.toLowerCase();
  const country = value(address, 'country');
  const isoCode = value(address, 'ISO3166-2-lvl4') || value(address, 'ISO3166-2-lvl3');
  const isJapanese = countryCode === 'jp'
    || isoCode?.toUpperCase().startsWith('JP-')
    || country === '日本'
    || country?.toLowerCase() === 'japan';
  if (!isJapanese) {
    return null;
  }

  const postcodeValue = value(address, 'postcode')?.replace(/^〒\s*/, '');
  const postcode = postcodeValue ? normalizePostcode(postcodeValue) : undefined;
  const prefecture = value(address, 'province')
    || value(address, 'state')
    || value(address, 'region')
    || prefectureFromIsoCode(address);
  const municipalityParts = [
    value(address, 'city'),
    value(address, 'municipality'),
    value(address, 'county'),
    value(address, 'city_district'),
    value(address, 'borough'),
    value(address, 'town'),
    value(address, 'village')
  ]
    .filter((part): part is string => !!part && part !== prefecture)
    .filter((part, index, self) => self.indexOf(part) === index);
  const locality = pickLocality(address);
  const cityBlock = value(address, 'city_block');
  const houseNumber = value(address, 'house_number');

  let localAddress = locality ? normalizeJapaneseAddressPart(locality) : '';
  if (cityBlock && houseNumber) {
    localAddress += `${normalizeJapaneseNumericPart(cityBlock)}－${normalizeJapaneseNumericPart(houseNumber)}`;
  } else if (cityBlock || houseNumber) {
    localAddress += normalizeJapaneseNumericPart(cityBlock || houseNumber || '');
  }

  const addressParts = [prefecture, ...municipalityParts, localAddress]
    .filter((part): part is string => !!part)
    .map(normalizeJapaneseAddressPart)
    .filter((part, index, self) => self.indexOf(part) === index);
  const parts = postcode ? [postcode, ...addressParts] : addressParts;

  return parts.length > 0 ? parts.join('　') : null;
}
